/**
 * W-5 · Réseau.
 *
 * L'information utile de cette page n'est PAS « combien de bus sont en ligne » :
 * c'est **depuis quand leur dernier point GPS date**. Un véhicule déclaré en
 * ligne dont la position remonte à quarante minutes est un bus perdu — il
 * s'affiche encore sur la carte de l'athlète, à un endroit où il n'est plus.
 * D'où la mise en avant de `oldestFixAgeSec` et le classement des véhicules par
 * fraîcheur décroissante : les plus douteux en premier.
 *
 * Cas Lenz indisponible : la passerelle est un système tiers. Une réponse
 * `{ available: false }` produit un bandeau, jamais une page en erreur — sinon
 * l'opérateur croit à une panne du back-office et appelle la mauvaise équipe.
 */

import { api } from '../lib/api';
import type { NetworkVehicle } from '../lib/api';
import { rangeKey } from '../lib/range';
import type { Range } from '../lib/range';
import { useApi, useTick } from '../lib/useApi';
import { formatAge, formatDateTime, formatDay, formatInt, formatSince } from '../lib/format';
import { Async, Badge, Banner, Card, Freshness, Kpi } from '../components/ui';
import type { Tone } from '../components/ui';
import { BarChart, LineChart } from '../charts';
import type { Categorical } from '../charts';
import { lineIdentity, sortLineNos } from '../lib/lines';
import { AlertsSection } from '../components/Alerts';

const LIVE_REFRESH_MS = 30_000;

/**
 * Seuils de fraîcheur. Ils viennent de l'usage, pas d'une norme : sous deux
 * minutes la position est exploitable pour une ETA ; jusqu'à dix minutes elle
 * situe encore le bus sur son tracé ; au-delà elle ne veut plus rien dire.
 */
const FRESH_SEC = 120;
const STALE_SEC = 600;

function freshnessTone(ageSec: number | undefined): Tone {
  if (ageSec === undefined || !Number.isFinite(ageSec)) return 'neutral';
  if (ageSec <= FRESH_SEC) return 'ok';
  if (ageSec <= STALE_SEC) return 'warn';
  return 'bad';
}

function freshnessWord(ageSec: number | undefined): string {
  if (ageSec === undefined || !Number.isFinite(ageSec)) return 'inconnu';
  if (ageSec <= FRESH_SEC) return 'frais';
  if (ageSec <= STALE_SEC) return 'en retard';
  return 'perdu';
}

export function NetworkPage({ range }: { range: Range }): React.ReactElement {
  const now = useTick(15_000);
  const lines = useApi((s) => api.networkLines(s), 'net-lines', { refreshMs: LIVE_REFRESH_MS });
  const vehicles = useApi((s) => api.networkVehicles(s), 'net-vehicles', { refreshMs: LIVE_REFRESH_MS });
  const health = useApi((s) => api.networkHealth(s), 'net-health', { refreshMs: LIVE_REFRESH_MS });
  const fleetSeries = useApi(
    (s) => api.timeseries('fleet_online', { from: range.from, to: range.to }, range.bucket, s),
    `fleet|${rangeKey(range)}`,
  );

  const formatX = range.bucket === 'hour' ? formatDateTime : formatDay;

  const lineRows = lines.data?.available === true ? lines.data.value : [];
  const vehicleRows = vehicles.data?.available === true ? vehicles.data.value : [];

  // Agrégat par ligne calculé depuis les véhicules quand `/network/lines` ne
  // porte pas les compteurs : les deux endpoints sont indépendants, l'un peut
  // répondre sans l'autre.
  const perLine = new Map<string, { online: number; oldest: number }>();
  for (const v of vehicleRows) {
    const current = perLine.get(v.lineNo) ?? { online: 0, oldest: 0 };
    current.online += 1;
    if (Number.isFinite(v.fixAgeSec)) current.oldest = Math.max(current.oldest, v.fixAgeSec);
    perLine.set(v.lineNo, current);
  }

  const lineNos = sortLineNos([
    ...new Set([...lineRows.map((l) => l.lineNo), ...perLine.keys()]),
  ]);

  const staleVehicles: NetworkVehicle[] = [...vehicleRows].sort(
    (a, b) => (Number.isFinite(b.fixAgeSec) ? b.fixAgeSec : -1) - (Number.isFinite(a.fixAgeSec) ? a.fixAgeSec : -1),
  );
  const lost = staleVehicles.filter((v) => Number.isFinite(v.fixAgeSec) && v.fixAgeSec > STALE_SEC).length;
  const oldest = staleVehicles[0]?.fixAgeSec;

  const fleetBars: Categorical[] = lineNos.map((lineNo, i) => {
    const identity = lineIdentity(lineNo, i);
    const fromCatalog = lineRows.find((l) => l.lineNo === lineNo)?.vehiclesOnline;
    return {
      key: lineNo,
      label: identity.name,
      value: fromCatalog ?? perLine.get(lineNo)?.online ?? 0,
      color: identity.color,
    };
  });

  const lenzDown =
    (lines.data?.available === false && vehicles.data?.available === false) ||
    (health.data?.available === true && !health.data.value.reachable);

  return (
    <div className="stack">
      {/* Les alertes réseau se lisent AVANT les chiffres qu'elles expliquent. */}
      <AlertsSection />
      {/* ─── Bandeau de disponibilité de la source ──────────────────────── */}
      {lines.data?.available === false || vehicles.data?.available === false ? (
        // Le `reason` renvoyé par l'API est écrit pour un technicien : il cite des
        // noms de variables d'environnement et le chemin d'un fichier. Il n'a rien
        // à faire sur l'écran d'un opérateur, qui n'y peut rien et n'y comprend
        // rien. Il reste consultable sur `/api/v1/health`.
        <Banner tone="warn" title="Positions des bus indisponibles">
          Le service qui fournit les lignes et les positions des bus ne répond pas en ce moment. Les
          chiffres de cette page sont donc incomplets. Le reste du tableau de bord (utilisateurs,
          usage, signalements) reste fiable. À faire : contacter l'exploitant de la passerelle.
        </Banner>
      ) : null}
      {lenzDown ? (
        <Banner tone="bad" title="Temps réel interrompu">
          Aucune position n'est servie. La carte de l'application affiche donc des bus figés ou aucun bus.
        </Banner>
      ) : null}

      {/* ─── Santé de la passerelle ─────────────────────────────────────── */}
      <Card
        title="Source des positions de bus"
        hint="État de la source réseau partagée avec l'application : c'est le même flux qui alimente la carte de l'athlète."
        action={
          <Freshness
            state={health}
            sinceLabel={
              health.updatedAt ? `sondé ${formatSince(new Date(health.updatedAt).toISOString(), now)}` : 'pas encore sondé'
            }
          />
        }
      >
        <Async state={health}>
          {(res) =>
            res.available === false ? (
              <Banner tone="warn" title="Service injoignable">
                Le back-office n'arrive pas à joindre le service qui fournit les lignes et les
                positions des bus. À faire : contacter son exploitant.
              </Banner>
            ) : (
              <div className="grid grid-kpi">
                <Kpi
                  label="Joignable"
                  value={res.value.reachable ? 'Oui' : 'Non'}
                  hint={res.value.status ? `État déclaré : ${res.value.status}.` : undefined}
                />
                <Kpi
                  label="Temps de réponse"
                  value={res.value.latencyMs === undefined ? 'n/d' : `${formatInt(res.value.latencyMs)} ms`}
                  hint="Temps de réponse mesuré par le back-office lors du dernier sondage."
                />
                <Kpi
                  label="Dernière mise à jour"
                  value={res.value.lastSyncAt ? formatDateTime(res.value.lastSyncAt) : 'jamais'}
                  hint="Heure de Dakar."
                />
              </div>
            )
          }
        </Async>
      </Card>

      {/* ─── Fraîcheur ──────────────────────────────────────────────────── */}
      <Card
        title="Positions des bus : depuis quand ?"
        hint={`Âge de la dernière position reçue pour chaque bus. À jour : moins de ${FRESH_SEC / 60} min · en retard : moins de ${STALE_SEC / 60} min · perdu au-delà.`}
        action={
          <Freshness
            state={vehicles}
            sinceLabel={
              vehicles.updatedAt ? `à jour ${formatSince(new Date(vehicles.updatedAt).toISOString(), now)}` : 'pas encore chargé'
            }
          />
        }
      >
        <Async state={vehicles}>
          {(res) =>
            res.available === false ? (
              <Banner tone="warn" title="Positions indisponibles">
                Aucune position de bus n'est transmise en ce moment. À faire : contacter
                l'exploitant de la passerelle.
              </Banner>
            ) : res.value.length === 0 ? (
              <Banner tone="warn" title="Aucun véhicule">
                Le service répond, mais ne transmet aucune position : soit les bus ne roulent pas, soit leurs boîtiers de localisation sont éteints.
              </Banner>
            ) : (
              <div className="stack">
                <div className="grid grid-kpi">
                  <Kpi
                    label="Bus en ligne"
                    value={formatInt(res.value.length)}
                    hint="Bus dont la position arrive effectivement jusqu’à l’application."
                  />
                  {/* La tuile pleine de cette page n'est PAS le nombre de bus
                      en ligne : c'est l'âge du pire point GPS. Un véhicule
                      déclaré en ligne dont la position remonte à quarante
                      minutes s'affiche encore sur la carte de l'athlète, à un
                      endroit où il n'est plus. */}
                  <Kpi
                    featured
                    label="Position la plus ancienne"
                    value={formatAge(oldest)}
                    hint="C'est le pire cas de la flotte : le chiffre à surveiller."
                  />
                  <Kpi
                    label="Bus perdus"
                    value={formatInt(lost)}
                    hint={`Véhicules dont le dernier point dépasse ${STALE_SEC / 60} minutes. Ils apparaissent encore sur la carte de l'athlète.`}
                  />
                </div>

                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Véhicule</th>
                        <th>Ligne</th>
                        <th className="num">Âge du point</th>
                        <th>État</th>
                        <th className="num">Vitesse</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staleVehicles.slice(0, 25).map((v, i) => {
                        const identity = lineIdentity(v.lineNo, i);
                        return (
                          <tr key={v.id}>
                            <td className="mono">{v.id}</td>
                            <td>
                              <span className="row">
                                <i className="dot-line" style={{ background: identity.color }} aria-hidden="true" />
                                {identity.name}
                              </span>
                            </td>
                            <td className="num">{formatAge(v.fixAgeSec)}</td>
                            <td>
                              <Badge tone={freshnessTone(v.fixAgeSec)}>{freshnessWord(v.fixAgeSec)}</Badge>
                            </td>
                            <td className="num">
                              {v.speedKmh === undefined ? 'n/d' : `${Math.round(v.speedKmh)} km/h`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {staleVehicles.length > 25 ? (
                  <p className="small muted" style={{ margin: 0 }}>
                    25 véhicules affichés sur {formatInt(staleVehicles.length)}, du plus douteux au plus frais.
                  </p>
                ) : null}
              </div>
            )
          }
        </Async>
      </Card>

      {/* ─── Flotte par ligne ───────────────────────────────────────────── */}
      <div className="grid grid-2">
        <Card title="Bus par ligne" hint="Dernier décompte connu, ligne par ligne.">
          {lines.data?.available === false && vehicles.data?.available === false ? (
            <Banner tone="warn" title="Positions indisponibles">
              Ni le catalogue ni les positions ne sont accessibles.
            </Banner>
          ) : (
            <BarChart title="Flotte en ligne par ligne" rows={fleetBars} labelWidth={120} />
          )}
        </Card>

        <Card
          title="Détail par ligne"
          hint="Ancienneté de la position la plus vieille, ligne par ligne. Une ligne entière peut décrocher sans que le total des bus ne bouge : c’est ici que ça se voit."
        >
          {lineNos.length === 0 ? (
            <Banner tone="warn" title="Aucune ligne">
              Aucune ligne n’est actuellement exploitée.
            </Banner>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Ligne</th>
                    <th className="num">En ligne</th>
                    <th className="num">Attendus</th>
                    <th className="num">Point le plus ancien</th>
                    <th>État</th>
                  </tr>
                </thead>
                <tbody>
                  {lineNos.map((lineNo, i) => {
                    const identity = lineIdentity(lineNo, i);
                    const catalog = lineRows.find((l) => l.lineNo === lineNo);
                    const derived = perLine.get(lineNo);
                    const online = catalog?.vehiclesOnline ?? derived?.online ?? 0;
                    const age = catalog?.oldestFixAgeSec ?? derived?.oldest;
                    return (
                      <tr key={lineNo}>
                        <td>
                          <span className="row">
                            <i className="dot-line" style={{ background: identity.color }} aria-hidden="true" />
                            {identity.name}
                            {catalog?.kind === 'test' ? <Badge>test</Badge> : null}
                          </span>
                        </td>
                        <td className="num">{formatInt(online)}</td>
                        <td className="num">
                          {catalog?.vehiclesExpected === undefined ? 'n/d' : formatInt(catalog.vehiclesExpected)}
                        </td>
                        <td className="num">{formatAge(age)}</td>
                        <td>
                          <Badge tone={freshnessTone(age)}>{freshnessWord(age)}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card
        title={`Flotte en ligne · ${range.label}`}
        hint="Relevé automatique toutes les minutes. L’historique reste consultable même si la source des positions a été interrompue entre-temps."
      >
        <Async state={fleetSeries}>
          {(d) => (
            <LineChart
              title="Flotte en ligne"
              formatX={formatX}
              height={240}
              series={[{ key: 'fleet', label: 'Véhicules en ligne', color: 'var(--c-green)', points: d.points }]}
            />
          )}
        </Async>
      </Card>
    </div>
  );
}
